import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaBars, FaBell, FaSignOutAlt, FaUserCircle,
  FaCalendarAlt, FaPhone, FaTimes, FaExclamationCircle,
  FaClock, FaCheckCircle, FaCheck,
} from "react-icons/fa";

// ✅ FIXED: API URL now points to the backend server
const API = "https://apigm.sriganapathymedical.shop/api";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Responsive hook ──
const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
};

const Header = ({ toggleSidebar, isOpen }) => {
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width <= 480;
  const isTablet = width > 480 && width <= 768;
  const isSmall = width <= 768; // mobile + tablet

  const user = JSON.parse(localStorage.getItem("user"));

  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_enquiries") || "[]"); }
    catch { return []; }
  });

  const [acceptedMsg, setAcceptedMsg] = useState(null);
  const acceptTimerRef = useRef(null);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/enquiries/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (_) { }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e) => {
      const { name } = e.detail;
      setShowPanel(true);
      setAcceptedMsg({ name });
      fetchNotifications();
      clearTimeout(acceptTimerRef.current);
      acceptTimerRef.current = setTimeout(() => {
        setAcceptedMsg(null);
        setShowPanel(false);
      }, 3000);
    };
    window.addEventListener("enquiry-accepted", handler);
    return () => {
      window.removeEventListener("enquiry-accepted", handler);
      clearTimeout(acceptTimerRef.current);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowPanel(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/");
  };

  const handleDismiss = (id) => {
    const updated = [...dismissed, id];
    setDismissed(updated);
    localStorage.setItem("dismissed_enquiries", JSON.stringify(updated));
  };

  const handleClearAll = () => {
    const ids = notifications.map((n) => n.id);
    const updated = [...dismissed, ...ids];
    setDismissed(updated);
    localStorage.setItem("dismissed_enquiries", JSON.stringify(updated));
    setShowPanel(false);
  };

  const visible = notifications.filter((n) => !dismissed.includes(n.id));
  const today = new Date().toISOString().split("T")[0];

  const getNotifType = (n) => {
    if (n.status === "Visited") return "visited";
    if (n.status === "Cancelled") return "cancelled";
    if (n.meetup_date < today) return "overdue";
    if (n.meetup_date === today) return "today";
    return "upcoming";
  };

  const notifColors = {
    overdue: { icon: <FaExclamationCircle />, color: "#ef4444", label: "OVERDUE", bg: "rgba(239,68,68,0.12)" },
    today: { icon: <FaCheckCircle />, color: "#fbbf24", label: "TODAY", bg: "rgba(251,191,36,0.12)" },
    upcoming: { icon: <FaClock />, color: "#4da6ff", label: "UPCOMING", bg: "rgba(77,166,255,0.08)" },
    visited: { icon: <FaCheckCircle />, color: "#22c55e", label: "VISITED", bg: "rgba(34,197,94,0.12)" },
    cancelled: { icon: <FaTimes />, color: "#9ca3af", label: "CANCELLED", bg: "rgba(156,163,175,0.12)" },
  };

  // ── Responsive panel width: full width on mobile, fixed on larger screens ──
  const panelWidth = isMobile
    ? "calc(100vw - 16px)"
    : isTablet
      ? "calc(100vw - 32px)"
      : 380;

  // Panel right offset: on mobile align to screen edge
  const panelRight = isMobile ? "auto" : 0;
  const panelLeft = isMobile ? "50%" : "auto";
  const panelTransform = isMobile ? "translateX(-50%)" : "none";

  return (
    <div style={{ ...styles.header, padding: isMobile ? "0 10px" : "0 16px" }}>
      {/* Left */}
      <div style={styles.left}>
        <button
          onClick={toggleSidebar}
          style={{
            background: "rgba(16,185,129,0.08)",
            border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: "8px",
            padding: isMobile ? "6px 8px" : "8px 10px",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.2)";
            e.currentTarget.style.borderColor = "rgba(16,185,129,0.6)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.08)";
            e.currentTarget.style.borderColor = "rgba(16,185,129,0.3)";
          }}
        >
          <span style={{ display: "block", width: isMobile ? "17px" : "20px", height: "2px", backgroundColor: "#10b981", borderRadius: "2px", transition: "all 0.3s ease", transform: isOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
          <span style={{ display: "block", width: isMobile ? "17px" : "20px", height: "2px", backgroundColor: "#10b981", borderRadius: "2px", transition: "all 0.3s ease", opacity: isOpen ? 0 : 1 }} />
          <span style={{ display: "block", width: isMobile ? "17px" : "20px", height: "2px", backgroundColor: "#10b981", borderRadius: "2px", transition: "all 0.3s ease", transform: isOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
        </button>
      </div>

      {/* Right */}
      <div style={{ ...styles.right, gap: isMobile ? "8px" : "14px" }}>

        {/* 🔔 Notification Bell */}
        <div ref={panelRef} style={{ position: "relative" }}>
          <button
            style={{
              ...styles.bellBtn,
              background: showPanel ? "rgba(77,166,255,0.2)" : "rgba(255,255,255,0.07)",
              padding: isMobile ? "7px 9px" : "8px 11px",
            }}
            onClick={() => setShowPanel((p) => !p)}
            title="Enquiry Notifications"
          >
            <FaBell style={{ fontSize: isMobile ? 16 : 18, color: visible.length > 0 || acceptedMsg ? "#fbbf24" : "#9ca3af" }} />
            {visible.length > 0 && !acceptedMsg && (
              <span style={styles.badge}>{visible.length > 9 ? "9+" : visible.length}</span>
            )}
          </button>

          {/* ── Dropdown Panel ── */}
          {showPanel && (
            <div style={{
              ...styles.panel,
              width: panelWidth,
              right: panelRight,
              left: panelLeft,
              transform: panelTransform,
              // On mobile, ensure it doesn't overflow the viewport
              maxWidth: isMobile ? "calc(100vw - 16px)" : 380,
            }}>

              {/* Panel Header */}
              <div style={styles.panelHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <FaBell style={{ color: "#fbbf24", flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: "#fff", fontSize: isMobile ? 13 : 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {isMobile ? "Reminders" : "Order & Enquiry Reminders"}
                  </span>
                  {visible.length > 0 && !acceptedMsg && (
                    <span style={styles.countPill}>{visible.length}</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  {visible.length > 0 && !acceptedMsg && (
                    <button style={{ ...styles.clearBtn, fontSize: isMobile ? 11 : 12, padding: isMobile ? "3px 8px" : "4px 10px" }} onClick={handleClearAll}>
                      {isMobile ? "Clear" : "Clear All"}
                    </button>
                  )}
                  <button style={styles.closeBtn} onClick={() => setShowPanel(false)}>
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* ── Accepted Success Banner ── */}
              {acceptedMsg && (
                <div style={{ ...styles.acceptedBanner, padding: isMobile ? "12px" : "16px", gap: isMobile ? 10 : 14 }}>
                  <div style={{ ...styles.acceptedIconWrap, width: isMobile ? 36 : 44, height: isMobile ? 36 : 44 }}>
                    <FaCheck style={{ color: "#fff", fontSize: isMobile ? 16 : 20 }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: isMobile ? 13 : 15, color: "#fff" }}>
                      Customer Accepted! ✅
                    </div>
                    <div style={{ fontSize: isMobile ? 11 : 13, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                      <strong style={{ color: "#86efac" }}>{acceptedMsg.name}</strong> has been marked as Visited.
                    </div>
                  </div>
                  <div style={styles.progressTrack}>
                    <div style={styles.progressBar} />
                  </div>
                </div>
              )}

              {/* ── Notification List ── */}
              {!acceptedMsg && (
                <div style={{ ...styles.panelBody, maxHeight: isMobile ? 280 : 360 }}>
                  {visible.length === 0 ? (
                    <div style={styles.emptyState}>
                      <FaCheckCircle style={{ fontSize: 32, color: "#22c55e", marginBottom: 8 }} />
                      <div style={{ color: "#6b7280", fontSize: 14 }}>All caught up! No active enquiries.</div>
                    </div>
                  ) : (
                    visible.map((n) => {
                      const type = getNotifType(n);
                      const cfg = notifColors[type];
                      return (
                        <div key={n.id} style={{ ...styles.notifItem, background: cfg.bg, borderLeft: `3px solid ${cfg.color}`, padding: isMobile ? "8px 10px" : "10px 12px" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                              <span style={{ color: cfg.color, fontSize: isMobile ? 12 : 14, flexShrink: 0 }}>{cfg.icon}</span>
                              <span style={{ fontWeight: 700, color: "#fff", fontSize: isMobile ? 12 : 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: isMobile ? "120px" : "160px" }}>{n.customer_name}</span>
                              <span style={{ ...styles.typeBadge, background: cfg.color }}>{cfg.label}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#9ca3af", fontSize: isMobile ? 11 : 12, marginBottom: 3, flexWrap: "wrap" }}>
                              <FaCalendarAlt style={{ color: cfg.color, flexShrink: 0 }} />
                              <span>Meet-up:</span>
                              <strong style={{ color: "#d1d5db" }}>{formatDate(n.meetup_date)}</strong>
                              {n.is_coming_today && (
                                <span style={{ color: "#22c55e", fontWeight: 700 }}>✅</span>
                              )}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#9ca3af", fontSize: isMobile ? 11 : 12, flexWrap: "wrap" }}>
                              <FaPhone style={{ color: "#4da6ff", flexShrink: 0 }} />
                              <span>{n.contact_number}</span>
                              {n.medical_interest && (
                                <span style={{ color: "#a855f7" }}>💊 {n.medical_interest}</span>
                              )}
                            </div>
                          </div>
                          <button style={{ ...styles.dismissBtn, padding: isMobile ? "3px 6px" : "4px 7px" }} onClick={() => handleDismiss(n.id)} title="Dismiss">
                            <FaTimes />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Footer */}
              {!acceptedMsg && visible.length > 0 && (
                <div style={styles.panelFooter}>
                  <button style={{ ...styles.viewAllBtn, fontSize: isMobile ? 12 : 13 }} onClick={() => { navigate("/enquiry"); setShowPanel(false); }}>
                    View All Enquiries →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User — hide name on mobile to save space */}
        {!isMobile && (
          <div style={styles.userSection}>
            <FaUserCircle />
            <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <span style={{ ...styles.username, fontSize: isTablet ? 13 : 14 }}>
                {user?.full_name || user?.username || "Admin"}
              </span>
            </div>
          </div>
        )}

        {/* Logout — icon only on mobile */}
        <button
          style={{
            ...styles.logoutBtn,
            padding: isMobile ? "7px 10px" : "6px 14px",
            borderRadius: isMobile ? "50%" : "20px",
            gap: isMobile ? 0 : "6px",
          }}
          onClick={handleLogout}
          title="Logout"
        >
          <FaSignOutAlt />
          {!isMobile && "Logout"}
        </button>
      </div>
    </div>
  );
};

// ───────────────── Styles ─────────────────
const styles = {
  header: {
    position: "fixed", top: 0, left: 0, right: 0, height: "65px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "0 16px 0 16px", background: "rgba(17,24,39,0.97)",
    backdropFilter: "blur(14px)", boxShadow: "0 2px 20px rgba(0,0,0,0.5)",
    zIndex: 2000, color: "#fff",
    boxSizing: "border-box",
  },
  left: { display: "flex", alignItems: "center", gap: "18px" },
  right: { display: "flex", alignItems: "center", gap: "14px" },

  bellBtn: {
    position: "relative", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10,
    padding: "8px 11px", cursor: "pointer", display: "flex", alignItems: "center",
    transition: "background 0.2s",
  },
  badge: {
    position: "absolute", top: -5, right: -5,
    background: "linear-gradient(135deg, #ef4444, #fbbf24)",
    color: "#fff", borderRadius: "50%", width: 18, height: 18,
    fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 0 8px rgba(239,68,68,0.6)",
    animation: "bellPulse 1.5s ease-in-out infinite",
  },

  panel: {
    position: "absolute", top: "calc(100% + 10px)",
    background: "linear-gradient(135deg, #111827, #1a2235)",
    border: "1px solid rgba(13,148,136,0.2)", borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.7)", zIndex: 3000,
    overflow: "hidden", animation: "dropIn 0.2s ease",
  },
  panelHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(0,0,0,0.2)", gap: 8,
  },
  countPill: {
    background: "rgba(251,191,36,0.2)", color: "#fbbf24",
    borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  clearBtn: {
    background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)",
    color: "#ef4444", borderRadius: 6, cursor: "pointer", fontSize: 12, padding: "4px 10px",
  },
  closeBtn: {
    background: "rgba(255,255,255,0.08)", border: "none", color: "#9ca3af",
    borderRadius: 6, cursor: "pointer", padding: "4px 8px", fontSize: 14,
  },

  acceptedBanner: {
    margin: 10,
    background: "linear-gradient(135deg, #14532d, #166534)",
    border: "1px solid rgba(34,197,94,0.4)",
    borderRadius: 12,
    padding: "16px",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    position: "relative",
    overflow: "hidden",
    animation: "fadeIn 0.3s ease",
  },
  acceptedIconWrap: {
    background: "rgba(34,197,94,0.3)",
    borderRadius: "50%",
    width: 44, height: 44,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
    border: "2px solid rgba(34,197,94,0.5)",
  },
  progressTrack: {
    position: "absolute", bottom: 0, left: 0, right: 0,
    height: 3, background: "rgba(255,255,255,0.1)",
  },
  progressBar: {
    height: "100%", background: "#86efac",
    animation: "toastProgress 3s linear forwards",
  },

  panelBody: { maxHeight: 360, overflowY: "auto", padding: "8px" },
  notifItem: {
    display: "flex", alignItems: "flex-start", gap: 10,
    borderRadius: 10, padding: "10px 12px", marginBottom: 6,
  },
  typeBadge: {
    fontSize: 9, fontWeight: 800, letterSpacing: 0.5, color: "#000",
    padding: "2px 6px", borderRadius: 4, flexShrink: 0,
  },
  dismissBtn: {
    background: "rgba(255,255,255,0.06)", border: "none", color: "#6b7280",
    borderRadius: 6, cursor: "pointer", padding: "4px 7px", fontSize: 11,
    flexShrink: 0,
  },
  emptyState: { textAlign: "center", padding: "30px 16px" },
  panelFooter: { padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,0.08)" },
  viewAllBtn: {
    width: "100%", background: "linear-gradient(135deg, rgba(16,185,129,0.1), #a855f722)",
    border: "1px solid rgba(16,185,129,0.3)", color: "#10b981",
    borderRadius: 8, padding: "9px", cursor: "pointer", fontWeight: 600, fontSize: 13,
  },

  userSection: {
    display: "flex", alignItems: "center", gap: "8px",
    backgroundColor: "#1f2937", padding: "6px 12px", borderRadius: "20px",
  },
  username: { fontSize: "14px", color: "#fff" },
  logoutBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
    padding: "6px 14px", backgroundColor: "#ef4444", color: "#fff",
    border: "none", borderRadius: "20px", cursor: "pointer", fontWeight: "500",
  },
};

export default Header;